import {defineStore} from 'pinia'
import axios from 'axios'
import {useAnalysisStore} from '@/stores/Setup/analysis'
import useWheelAnalysisQuery from '@/composables/useWheelAnalysisQuery'
import enGoals from '@/i18n/locales/en_criticalGoals.json'
import esGoals from '@/i18n//locales/es_criticalGoals.json'
import zhHansGoals from '@/i18n/locales/zh-hans_criticalGoals.json'

export const useCriticalGoalsStore = defineStore('criticalGoals', {
    state: () => ({
        hydratedOnce: false,
        isHydrating: false,
        goals: enGoals.goals,
        selectedCriticalGoals: Array(17).fill(false), // 17 goals
        goalAIndex: null,
        goalBIndex: null,
    }),

    actions: {
        // ---------- hydration ----------
        async fetchWheelAnalysis() {
            await useWheelAnalysisQuery()
        },
        setLocale(locale) {
            switch (locale) {
                case 'es':
                    this.goals = esGoals.goals
                    break
                case 'zh-hans':
                    this.goals = zhHansGoals.goals
                    break
                case 'en':
                default:
                    this.goals = enGoals.goals
            }
        },

        setSelectedCriticalGoals(next) {
            // enforce boolean copy, no shared refs
            const bools = (next || []).map(v => v === true || v === 'true')
            this.selectedCriticalGoals = [...bools]
            this.selectedCriticalGoalsChanged()
        },

        setCriticalGoalLocal(index, value) {
            const rowCopy = [...this.selectedCriticalGoals]
            rowCopy[index] = (value === true || value === 'true')
            this.selectedCriticalGoals = rowCopy
            this.selectedCriticalGoalsChanged()
        },

        // ---------- changed handler + saves ----------
        selectedCriticalGoalsChanged() {
            if (this.isHydrating) return

            const analysisStore = useAnalysisStore()
            const id = analysisStore.wheelAnalysisId
            if (id == null) {
                console.warn('[criticalGoals] save skipped: wheelAnalysisId missing')
                return
            }

            // normalize to booleans
            const bools = this.selectedCriticalGoals.map(v => v === true || v === 'true')

            // compute A (first true) and B (last true)
            const firstIdx = bools.findIndex(v => v)
            const lastIdx = bools.lastIndexOf(true)

            this.goalAIndex = firstIdx >= 0 ? firstIdx : null
            this.goalBIndex = lastIdx >= 0 ? lastIdx : null

            // explicit saves (mirroring allGoals explicit methods)
            this.saveSelectedCriticalGoals(id, bools)
            this.saveGoalAIndex(id, this.goalAIndex)
            this.saveGoalBIndex(id, this.goalBIndex)
        },

        // ---------- explicit save methods ----------
        saveSelectedCriticalGoals(wheelId, bools = null) {
            if (!wheelId) return
            const payload = {
                selected_critical_goals: JSON.stringify(
                    (bools ?? this.selectedCriticalGoals).map(v => v === true || v === 'true')
                )
            }
            axios.put(
                `${process.env.VUE_APP_API_ROOT}api/v1/wheel_analyses/${wheelId}/selected_critical_goals`,
                payload
            ).then(r => console.log('Saved selected_critical_goals', r.status))
                .catch(e => console.error('Save failed (selected_critical_goals):', e))
        },

        saveGoalAIndex(wheelId, idx = this.goalAIndex) {
            if (!wheelId || idx === null) return
            axios.put(
                `${process.env.VUE_APP_API_ROOT}api/v1/wheel_analyses/${wheelId}/goal_a_index`,
                {goal_a_index: idx}
            ).then(r => console.log('Saved goal_a_index', r.status))
                .catch(e => console.error('Save failed (goal_a_index):', e))
        },

        saveGoalBIndex(wheelId, idx = this.goalBIndex) {
            if (!wheelId || idx === null) return
            axios.put(
                `${process.env.VUE_APP_API_ROOT}api/v1/wheel_analyses/${wheelId}/goal_b_index`,
                {goal_b_index: idx}
            ).then(r => console.log('Saved goal_b_index', r.status))
                .catch(e => console.error('Save failed (goal_b_index):', e))
        },
    },

    persist: true
})